"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import { useLocale } from "@/components/locale-provider";
import { IconPhoto, IconSearch } from "@/components/icons";

export type ExchangeTarget = { type: "ITEM" | "BOOK"; id: string; title: string };

type ItemHit = {
  id: string;
  title: string;
  artist: string;
  type: "CD" | "CASSETTE" | "VINYL";
  coverImageUrl: string | null;
};

type BookHit = {
  id: string;
  title: string;
  author: string;
  format: "HARDCOVER" | "PAPERBACK" | "OTHER";
  coverImageUrl: string | null;
};

type Hit = {
  target: ExchangeTarget;
  subtitle: string;
  typeLabel: string;
  coverImageUrl: string | null;
};

const PICKER_PAGE_SIZE = 8;

export function ExchangePicker({
  username,
  excludeType,
  excludeId,
  onSelect,
  onNavigateAway,
}: {
  username: string;
  excludeType: "ITEM" | "BOOK";
  excludeId: string;
  onSelect: (target: ExchangeTarget) => void;
  onNavigateAway?: () => void;
}) {
  const { t } = useLocale();
  const { data: session } = useSession();
  const booksEnabled = session?.user.booksEnabled ?? false;
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<Hit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!q) {
      setHits(null);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const [itemsRes, booksRes] = await Promise.all([
          fetch(`/api/items?q=${encodeURIComponent(q)}&pageSize=${PICKER_PAGE_SIZE}`),
          booksEnabled ? fetch(`/api/books?q=${encodeURIComponent(q)}&pageSize=${PICKER_PAGE_SIZE}`) : null,
        ]);
        const itemHits: Hit[] = itemsRes.ok
          ? (await itemsRes.json()).items
              .filter((i: ItemHit) => !(excludeType === "ITEM" && i.id === excludeId))
              .map((i: ItemHit) => ({
                target: { type: "ITEM" as const, id: i.id, title: i.title },
                subtitle: i.artist,
                typeLabel: t.types[i.type],
                coverImageUrl: i.coverImageUrl,
              }))
          : [];
        const bookHits: Hit[] =
          booksRes && booksRes.ok
            ? (await booksRes.json()).books
                .filter((b: BookHit) => !(excludeType === "BOOK" && b.id === excludeId))
                .map((b: BookHit) => ({
                  target: { type: "BOOK" as const, id: b.id, title: b.title },
                  subtitle: b.author,
                  typeLabel: t.books.formats[b.format],
                  coverImageUrl: b.coverImageUrl,
                }))
            : [];
        setHits([...itemHits, ...bookHits]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, [query, excludeType, excludeId, booksEnabled, t]);

  return (
    <div className="pt-2">
      <div className="flex items-center gap-2 rounded-full bg-ios-fill px-3.5 py-2">
        <IconSearch className="h-4 w-4 shrink-0 text-ios-label-secondary" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t.removalReason.exchangeSearchPlaceholder}
          className="w-full bg-transparent text-[16px] text-ios-label outline-none placeholder:text-ios-label-tertiary"
        />
      </div>

      {searching && <p className="pt-2 text-[12px] text-ios-label-secondary">{t.removalReason.exchangeSearching}</p>}

      {!searching && hits && hits.length === 0 && (
        <p className="pt-2 text-[12px] text-ios-label-secondary">{t.removalReason.exchangeNoResults}</p>
      )}

      {!searching && hits && hits.length > 0 && (
        <ul className="mt-2 overflow-hidden rounded-ios-md border border-ios-separator">
          {hits.map((hit, i) => (
            <li key={`${hit.target.type}-${hit.target.id}`} className={i > 0 ? "border-t border-ios-separator" : ""}>
              <button
                type="button"
                onClick={() => onSelect(hit.target)}
                className="flex w-full items-center gap-3 px-3 py-2.5 text-left active:bg-ios-fill"
              >
                {hit.coverImageUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={hit.coverImageUrl} alt="" className="h-10 w-10 shrink-0 rounded-[6px] object-cover"
  loading="lazy"
  decoding="async"
/>
                ) : (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[6px] bg-ios-fill">
                    <IconPhoto className="h-4 w-4 text-ios-label-tertiary" />
                  </div>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] text-ios-label">{hit.target.title}</span>
                  <span className="block truncate text-[12px] text-ios-label-secondary">
                    {[hit.subtitle, hit.typeLabel].filter(Boolean).join(" · ")}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex flex-col gap-1.5">
        <Link
          href={`/${username}/items/new?resumeExchangeFor=${excludeType}:${excludeId}`}
          onClick={onNavigateAway}
          className="text-[13px] font-medium text-ios-accent"
        >
          {t.removalReason.exchangeAddNewRecord}
        </Link>
        {booksEnabled && (
          <Link
            href={`/${username}/books/new?resumeExchangeFor=${excludeType}:${excludeId}`}
            onClick={onNavigateAway}
            className="text-[13px] font-medium text-ios-accent"
          >
            {t.removalReason.exchangeAddNewBook}
          </Link>
        )}
      </div>
    </div>
  );
}
